import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Popover, Typography, Button, CircularProgress, Tooltip } from '@material-ui/core';

import { useAiCredits } from '../../context/AiCredits/AiCreditsContext';

const useStyles = makeStyles(() => ({
  wrap: {
    position: 'relative',
    width: 36,
    height: 36,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  track: {
    position: 'absolute',
    color: '#e2e8f0',
  },
  value: {
    position: 'absolute',
  },
  label: {
    position: 'absolute',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
  },
  popover: {
    padding: 16,
    width: 260,
    borderRadius: 14,
  },
  popTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: '#0f172a',
    marginBottom: 10,
  },
  bigPct: {
    fontSize: 30,
    fontWeight: 800,
    lineHeight: 1,
  },
  bigLabel: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 12,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 12.5,
    color: '#334155',
    padding: '3px 0',
    '& b': { fontWeight: 700, color: '#0f172a' },
  },
  warn: {
    fontSize: 12,
    fontWeight: 600,
    padding: '8px 10px',
    borderRadius: 8,
    margin: '4px 0 12px',
  },
  btn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    boxShadow: 'none',
  },
}));

const colorForPct = (pct, exhausted) => {
  if (exhausted || pct < 10) return '#dc2626';
  if (pct < 50) return '#f59e0b';
  return '#16a34a';
};

/**
 * Chart circular de créditos de IA na barra superior. Só aparece para o OWNER
 * de planos com crédito de plataforma (quem acessa/recarrega em Assinaturas).
 * Carrega o saldo 1x no mount e atualiza em tempo real por socket a cada débito
 * (evento company-{id}-ai-credits) — sem polling.
 */
const AiCreditsBadge = () => {
  const classes = useStyles();
  const history = useHistory();
  // Fonte única: fetch + socket vivem no AiCreditsProvider. Aqui só consumimos.
  const { enabled: shouldShow, credits } = useAiCredits();
  const [anchorEl, setAnchorEl] = useState(null);

  const { pct, color, exhausted } = useMemo(() => {
    const bal = credits?.totalBalance ?? 0;
    // Referência de 100% = incluído do plano + extras. Assim os créditos extras
    // entram na conta e a % reflete o pool completo (não satura em 100% só por
    // ter extras). Denominador 0 → sem base: 100% se há saldo, 0% se não.
    const denom = (credits?.planIncluded || 0) + (credits?.extraBalance || 0);
    const p = denom > 0 ? Math.max(0, Math.min(100, (bal / denom) * 100)) : bal > 0 ? 100 : 0;
    const ex = bal <= 0;
    return { pct: p, color: colorForPct(p, ex), exhausted: ex };
  }, [credits]);

  const brl = (v) =>
    Number(v || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

  if (!shouldShow || credits == null) return null;

  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="Créditos de IA" arrow>
        <Box className={classes.wrap} onMouseEnter={openPopover} onClick={openPopover}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={34}
            thickness={4}
            className={classes.track}
          />
          <CircularProgress
            variant="determinate"
            value={pct}
            size={34}
            thickness={4}
            className={classes.value}
            style={{ color }}
          />
          <span className={classes.label} style={{ color }}>
            {exhausted ? '!' : `${Math.round(pct)}%`}
          </span>
        </Box>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ className: classes.popover, onMouseLeave: closePopover }}
      >
        <Typography className={classes.popTitle}>Créditos de IA</Typography>
        <Box className={classes.bigPct} style={{ color }}>
          {Math.round(pct)}%
        </Box>
        <Typography className={classes.bigLabel}>restante (plano + extras)</Typography>

        <Box className={classes.row}>
          <span>Disponível</span>
          <b>{brl(credits.totalBalance)}</b>
        </Box>
        <Box className={classes.row}>
          <span>Plano</span>
          <span>{brl(credits.planBalance)}</span>
        </Box>
        {credits.extraBalance > 0 && (
          <Box className={classes.row}>
            <span>Extras</span>
            <span>{brl(credits.extraBalance)}</span>
          </Box>
        )}

        {exhausted ? (
          <Typography
            className={classes.warn}
            style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}
          >
            Créditos esgotados — os agentes de IA estão pausados.
          </Typography>
        ) : pct < 10 ? (
          <Typography
            className={classes.warn}
            style={{ backgroundColor: '#fef3c7', color: '#b45309' }}
          >
            Menos de 10% dos créditos restantes.
          </Typography>
        ) : null}

        <Button
          fullWidth
          variant="contained"
          color="primary"
          className={classes.btn}
          onClick={() => {
            closePopover();
            history.push('/subscription/creditos');
          }}
        >
          Recarregar créditos
        </Button>
      </Popover>
    </>
  );
};

export default AiCreditsBadge;
